/* =======================================================================
   Knowledge Base — search box with typeahead. Azure item 416.

   Behaviour copied from the Work Orders search: filters live as you type,
   suggests matches in a dropdown, and clears with the ⊗ inside the field.
   The placeholder says "Search Knowledge Base" on purpose, to keep it
   distinct from the app's own search in the header.

   The parent owns the query (it drives the tree and the results column);
   this component owns only the dropdown and its keyboard selection.
   ======================================================================= */
import { useEffect, useRef, useState } from 'react';
import { matches } from './search';

export default function KbSearchBox({ value, onChange, onOpen, onSubmit, onClear, inputRef }){
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(-1);
  const listRef = useRef(null);

  const term = (value || '').trim();
  const res = term.length >= 2 ? matches(term) : [];
  const items = res.slice(0, 6);

  /* a new term resets the keyboard cursor */
  useEffect(() => { setSel(-1); }, [term]);

  const moveSel = d => {
    if (!items.length) return;
    const n = (sel + d + items.length) % items.length;
    setSel(n);
    const el = listRef.current && listRef.current.children[n];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = e => {
    if (e.key === 'ArrowDown'){ e.preventDefault(); moveSel(1); }
    else if (e.key === 'ArrowUp'){ e.preventDefault(); moveSel(-1); }
    else if (e.key === 'Enter'){
      e.preventDefault();
      if (sel > -1 && items[sel]) onOpen(items[sel].a);
      else { setOpen(false); onSubmit(); e.target.blur(); }
    } else if (e.key === 'Escape'){ onClear(); e.target.blur(); }
  };

  return (
    <div className="topsearch">
      <svg className="mag" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search Knowledge Base"
        autoComplete="off"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
      />
      <span className={'ts-kbd' + (term ? ' off' : '')}>/</span>
      <button className={'ts-clear' + (term ? ' on' : '')} type="button" title="Clear search"
        onMouseDown={e => { e.preventDefault(); onClear(); inputRef.current && inputRef.current.focus(); }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9.5"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
      </button>

      <div className={'ts-drop' + (open && term.length >= 2 ? ' on' : '')} ref={listRef}>
        {term.length >= 2 && !res.length && (
          <div className="ts-none">No articles match <b>&ldquo;{term}&rdquo;</b>.<br/>Press Enter to log it as an unanswered search.</div>
        )}
        {items.map((r, i) => (
          <div
            key={r.a.slug}
            className={'ts-item' + (i === sel ? ' sel' : '')}
            onMouseDown={e => { e.preventDefault(); onOpen(r.a); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 3v5h5"/><path d="M19 8v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7z"/></svg>
            <div><div className="t">{r.a.title}</div><div className="m">{r.a.module}</div></div>
          </div>
        ))}
        {res.length > 6 && (
          <div className="ts-more" onMouseDown={e => { e.preventDefault(); setOpen(false); onSubmit(); }}>
            See all {res.length} results →
          </div>
        )}
      </div>
    </div>
  );
}
